import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Simple password protection
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || '';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('amv_admin_auth', 'true');
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('amv_admin_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/contacts`)
      ]);
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('amv_admin_auth');
  };

  const getUniqueSources = () => {
    const sources = leads.map(l => l.source || 'unknown');
    return ['all', ...new Set(sources)];
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.business_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
    
    return matchesSearch && matchesSource;
  });

  const filteredContacts = contacts.filter(contact => {
    return contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           contact.company?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]).filter(k => k !== 'id');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(h => {
          const val = row[h] || '';
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceLabel = (source) => {
    if (!source) return 'Unknown';
    if (source.startsWith('resource_download_')) {
      return '📥 ' + source.replace('resource_download_', '').replace(/_/g, ' ');
    }
    if (source === 'enquiry_form') return '📝 Enquiry Form';
    if (source === 'contact_form') return '📞 Contact Form';
    return source;
  };

  const getSourceColor = (source) => {
    if (!source) return 'bg-gray-500/20 text-gray-400';
    if (source.startsWith('resource_download_')) return 'bg-green-500/20 text-green-400';
    if (source === 'enquiry_form') return 'bg-purple-500/20 text-purple-400';
    if (source === 'contact_form') return 'bg-blue-500/20 text-blue-400';
    return 'bg-gray-500/20 text-gray-400';
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4">
        <div className="bg-[#161616] rounded-2xl p-8 max-w-md w-full border border-white/10">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">AMV Admin Dashboard</h1>
            <p className="text-gray-400">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              placeholder="Admin Password"
              className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Header */}
      <header className="bg-[#161616] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">AMV Admin Dashboard</h1>
            <p className="text-gray-400 text-sm">Lead & Form Submission Tracking</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm hover:bg-white/10 transition-colors"
            >
              ↻ Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#161616] rounded-xl p-6 border border-white/10">
            <p className="text-gray-400 text-sm mb-1">Total Leads</p>
            <p className="text-3xl font-bold text-purple-400">{leads.length}</p>
          </div>
          <div className="bg-[#161616] rounded-xl p-6 border border-white/10">
            <p className="text-gray-400 text-sm mb-1">Contact Submissions</p>
            <p className="text-3xl font-bold text-blue-400">{contacts.length}</p>
          </div>
          <div className="bg-[#161616] rounded-xl p-6 border border-white/10">
            <p className="text-gray-400 text-sm mb-1">Resource Downloads</p>
            <p className="text-3xl font-bold text-green-400">
              {leads.filter(l => l.source?.startsWith('resource_download_')).length}
            </p>
          </div>
          <div className="bg-[#161616] rounded-xl p-6 border border-white/10">
            <p className="text-gray-400 text-sm mb-1">Enquiry Forms</p>
            <p className="text-3xl font-bold text-orange-400">
              {leads.filter(l => l.source === 'enquiry_form').length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-6 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'leads'
                ? 'bg-purple-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'contacts'
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            Contact Forms ({contacts.length})
          </button>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder="Search by name, email, company..."
            className="flex-1 bg-[#161616] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {activeTab === 'leads' && (
            <select
              className="bg-[#161616] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              {getUniqueSources().map(source => (
                <option key={source} value={source}>
                  {source === 'all' ? 'All Sources' : getSourceLabel(source)}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => exportToCSV(
              activeTab === 'leads' ? filteredLeads : filteredContacts,
              activeTab === 'leads' ? 'amv_leads' : 'amv_contacts'
            )}
            className="px-6 py-3 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl font-medium hover:bg-green-500/20 transition-colors whitespace-nowrap"
          >
            📥 Export CSV
          </button>
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : (
          <div className="bg-[#161616] rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Name</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Phone</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      {activeTab === 'leads' ? 'Business/Company' : 'Company'}
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Country</th>
                    {activeTab === 'leads' && (
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Source</th>
                    )}
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {(activeTab === 'leads' ? filteredLeads : filteredContacts).map((item, idx) => (
                    <tr key={item.id || idx} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-medium">{item.name || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <a href={`mailto:${item.email}`} className="text-purple-400 hover:underline">
                          {item.email || 'N/A'}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {item.phone ? (
                          <a href={`tel:${item.phone}`} className="hover:text-white">
                            {item.phone}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {item.business_description || item.company || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-300">{item.country || 'N/A'}</td>
                      {activeTab === 'leads' && (
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getSourceColor(item.source)}`}>
                            {getSourceLabel(item.source)}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 text-gray-400 text-sm">
                        {formatDate(item.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {(activeTab === 'leads' ? filteredLeads : filteredContacts).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No {activeTab === 'leads' ? 'leads' : 'contact submissions'} found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Message Preview for leads with messages */}
        {activeTab === 'leads' && filteredLeads.some(l => l.message) && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Recent Messages</h3>
            <div className="space-y-4">
              {filteredLeads.filter(l => l.message && !l.message.startsWith('Downloaded:')).slice(0, 5).map((lead) => (
                <div key={lead.id || lead.email} className="bg-[#161616] rounded-xl p-6 border border-white/10">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="font-medium">{lead.name}</span>
                      <span className="text-gray-400 text-sm ml-2">({lead.email})</span>
                    </div>
                    <span className="text-gray-500 text-sm">{formatDate(lead.created_at)}</span>
                  </div>
                  <p className="text-gray-300">{lead.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
